/**
 * The billing tab's view of a proposal: its packages and extras, whether the
 * customer has opened it, and what they signed for.
 *
 * A proposal's lines are edited in the builder and nowhere else. The plain
 * line table would save them without their packages, so on a proposal it is
 * replaced by `ProposalLines`, which only reads. Decision 80.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  availableAddOns,
  declineReasonLabel,
  proposalChoiceProblem,
  proposalTotals,
} from '@fieldops/shared'
import { cn } from '../../lib/cn.js'
import { fileUrl } from '../../lib/files.js'
import { formatDateTime, money, relativeTime } from '../../lib/format.js'
import { errorMessage, useTRPC, type RouterOutputs } from '../../lib/trpc.js'
import { Modal, ModalCloseButton } from '../Modal.js'
import { Button, Field, Input, buttonClass } from '../ui.js'
import { Label, Panel, PanelHeading } from './panes.js'

type QuoteDetail = RouterOutputs['quotes']['byId']
type QuoteLine = QuoteDetail['lines'][number]

/** A quote the builder wrote: it has parts or packages. */
export function isProposalQuote(quote: { sections: unknown[]; options: unknown[] }): boolean {
  return quote.sections.length > 0 || quote.options.length > 0
}

/** Whether accepting it involves a choice the office has to make. */
export function acceptNeedsChoice(quote: { options: unknown[]; lines: Array<{ isOptional: boolean }> }): boolean {
  return quote.options.length > 0 || quote.lines.some((line) => line.isOptional)
}

function quantityNote(line: QuoteLine): string {
  const value = Number(line.quantity.toString())
  return Number.isFinite(value) && value !== 1 ? ` × ${value}` : ''
}

export function ProposalLines({ quote }: { quote: QuoteDetail }) {
  const acceptance = quote.acceptance
  const chosen = new Set(acceptance?.addOnLineIds ?? [])
  const totals = proposalTotals({ lines: quote.lines, options: quote.options, discountAmount: quote.discountAmount })
  const included = quote.lines.filter((line) => !line.optionId && !line.isOptional)
  const extras = quote.lines.filter((line) => line.isOptional)
  const hasPackages = quote.options.length > 0

  return (
    <div className="flex flex-col gap-3 px-4 py-3.5">
      {included.length > 0 ? (
        <Group title={hasPackages ? 'In every option' : 'The work'} lines={included} />
      ) : null}

      {quote.options.map((option) => {
        const lines = quote.lines.filter((line) => line.optionId === option.id && !line.isOptional)
        const price = totals.packages.find((entry) => entry.optionId === option.id)
        const taken = acceptance?.optionId === option.id
        return (
          <div
            key={option.id}
            className={cn(
              'rounded-lg px-3 py-2.5 ring-1',
              taken ? 'bg-brand-50 ring-brand-600' : acceptance ? 'opacity-60 ring-ink-200' : 'ring-ink-200',
            )}
          >
            <div className="flex items-baseline gap-2">
              <span className="text-[13px] font-semibold text-ink-900">{option.name}</span>
              {option.isRecommended ? <span className="text-[11px] text-ink-500">Recommended</span> : null}
              {taken ? <span className="text-[11px] font-semibold text-brand-700">Chosen</span> : null}
              <span className="ml-auto text-[13px] font-semibold tabular-nums text-ink-900">
                {money(price?.totalIncTax ?? 0)}
              </span>
            </div>
            {lines.length > 0 ? <LineRows lines={lines} /> : null}
          </div>
        )
      })}

      {extras.length > 0 ? (
        <div>
          <Label>Optional extras</Label>
          <ul className="mt-1 flex flex-col divide-y divide-ink-100">
            {extras.map((line) => {
              const option = quote.options.find((entry) => entry.id === line.optionId)
              const taken = chosen.has(line.id)
              return (
                <li key={line.id} className="flex items-baseline gap-2 py-1.5 text-[13px]">
                  <span className={cn('min-w-0 flex-1 truncate', acceptance && !taken ? 'text-ink-400' : 'text-ink-800')}>
                    {line.description}
                    {quantityNote(line)}
                    {option ? <span className="text-ink-500"> — with {option.name}</span> : null}
                  </span>
                  {taken ? <span className="text-[11px] font-semibold text-brand-700">Ticked</span> : null}
                  <span className="shrink-0 tabular-nums text-ink-700">+{money(line.lineTotalIncTax)}</span>
                </li>
              )
            })}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

function Group({ title, lines }: { title: string; lines: QuoteLine[] }) {
  return (
    <div>
      <Label>{title}</Label>
      <LineRows lines={lines} />
    </div>
  )
}

function LineRows({ lines }: { lines: QuoteLine[] }) {
  return (
    <ul className="mt-1 flex flex-col divide-y divide-ink-100">
      {lines.map((line) => (
        <li key={line.id} className="flex items-baseline gap-2 py-1.5 text-[13px]">
          <span className="min-w-0 flex-1 truncate text-ink-800">
            {line.description}
            {quantityNote(line)}
          </span>
          <span className="shrink-0 tabular-nums text-ink-700">{money(line.lineTotalIncTax)}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * The figures on the rail. What was accepted once something was; before that
 * the quote's own totals, which are the default package with no extras.
 */
export function proposalRailTotals(quote: QuoteDetail): {
  rows: Array<[string, number | null]>
  total: [string, number]
} {
  const acceptance = quote.acceptance
  if (acceptance) {
    return {
      rows: [
        ['Subtotal', acceptance.acceptedSubtotalExTax],
        ['GST', acceptance.acceptedTax],
      ],
      total: ['Accepted', acceptance.acceptedTotalIncTax],
    }
  }

  const rows: Array<[string, number | null]> = [
    ['Subtotal', quote.subtotalExTax],
    ['GST', quote.totalTax],
  ]
  if (quote.discountAmount) rows.push(['Discount', -quote.discountAmount])
  return { rows, total: [quote.options.length > 0 ? 'Default option' : 'Total', quote.totalIncTax] }
}

/** "Opened twice, last an hour ago", from the live link. */
export function ViewedNote({ quote }: { quote: QuoteDetail }) {
  const viewed = quote.shareLinks.filter((link) => link.firstViewedAt)
  if (viewed.length === 0) {
    const live = quote.shareLinks.some((link) => !link.revokedAt)
    return live && !quote.acceptance ? (
      <p className="text-xs text-ink-500">The customer has not opened their link yet.</p>
    ) : null
  }

  const views = viewed.reduce((sum, link) => sum + link.viewCount, 0)
  const last = viewed
    .map((link) => link.lastViewedAt ?? link.firstViewedAt!)
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]

  return (
    <p className="text-xs text-ink-600">
      Opened by the customer {views === 1 ? 'once' : `${views} times`}, last {relativeTime(last)}.
    </p>
  )
}

export function AcceptancePanel({ quote }: { quote: QuoteDetail }) {
  const trpc = useTRPC()
  const acceptance = quote.acceptance
  const render = useMutation(
    trpc.quotes.acceptanceDocument.mutationOptions({
      onSuccess: (result) => {
        const url = fileUrl({ id: result.attachmentId, checksum: 'stored' })
        if (url) window.open(url, '_blank', 'noreferrer')
      },
    }),
  )

  if (!acceptance) return null

  const signature = quote.acceptedSignature ? fileUrl(quote.acceptedSignature) : null
  const document = acceptance.document ? fileUrl(acceptance.document) : null
  const extras = quote.lines.filter((line) => acceptance.addOnLineIds.includes(line.id))

  return (
    <Panel>
      <PanelHeading
        title="Accepted"
        hint={acceptance.channel === 'ONLINE' ? 'Signed online' : 'Recorded by the office'}
      />
      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-[13px]">
        <dt className="text-ink-500">By</dt>
        <dd className="text-ink-900">{acceptance.signedName}</dd>
        <dt className="text-ink-500">When</dt>
        <dd className="text-ink-900">{formatDateTime(acceptance.acceptedAt)}</dd>
        {acceptance.option ? (
          <>
            <dt className="text-ink-500">Option</dt>
            <dd className="text-ink-900">{acceptance.option.name}</dd>
          </>
        ) : null}
        {extras.length > 0 ? (
          <>
            <dt className="text-ink-500">Extras</dt>
            <dd className="text-ink-900">{extras.map((line) => line.description).join(', ')}</dd>
          </>
        ) : null}
        <dt className="text-ink-500">Total</dt>
        <dd className="font-semibold tabular-nums text-ink-900">{money(acceptance.acceptedTotalIncTax)}</dd>
      </dl>
      {signature ? (
        <img src={signature} alt={`Signature of ${acceptance.signedName}`} className="mt-3 h-16 w-auto" />
      ) : null}
      <div className="mt-3 flex flex-wrap gap-2">
        {document ? (
          <a href={document} target="_blank" rel="noreferrer" className={buttonClass({ size: 'sm' })}>
            The signed copy
          </a>
        ) : (
          <Button size="sm" loading={render.isPending} onClick={() => render.mutate({ id: quote.id })}>
            Make the signed copy
          </Button>
        )}
        {render.error ? (
          <span role="alert" className="text-xs text-red-600">
            {errorMessage(render.error)}
          </span>
        ) : null}
      </div>
    </Panel>
  )
}

export function DeclinedNote({ quote }: { quote: QuoteDetail }) {
  if (quote.status !== 'DECLINED') return null
  const reason = declineReasonLabel(quote.declineCategory)
  if (!reason && !quote.declineReason) return null

  return (
    <Panel>
      <PanelHeading title="Declined" hint={quote.respondedAt ? formatDateTime(quote.respondedAt) : undefined} />
      {reason ? <p className="text-[13px] font-medium text-ink-900">{reason}</p> : null}
      {quote.declineReason ? (
        <p className="mt-1 text-[13px] whitespace-pre-wrap text-ink-700">{quote.declineReason}</p>
      ) : null}
    </Panel>
  )
}

/**
 * A yes by phone on a proposal: which option, which extras, who said it.
 *
 * The same `applyAcceptedProposal` as the customer's own page, so the job
 * fills in the same way — only without a signature.
 */
export function OfficeAcceptPopup({
  quote,
  open,
  onClose,
}: {
  quote: QuoteDetail
  open: boolean
  onClose: () => void
}) {
  return (
    <Modal open={open} onClose={onClose} labelledBy="office-accept-title" className="max-w-[520px]">
      {open ? <OfficeAccept quote={quote} onClose={onClose} /> : null}
    </Modal>
  )
}

function OfficeAccept({ quote, onClose }: { quote: QuoteDetail; onClose: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const fallback =
    quote.options.find((option) => option.isRecommended)?.id ?? quote.options[0]?.id ?? null
  const [optionId, setOptionId] = useState<string | null>(fallback)
  const [addOnIds, setAddOnIds] = useState<string[]>([])
  const [name, setName] = useState('')

  const respond = useMutation(
    trpc.quotes.respond.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.quotes.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        onClose()
      },
    }),
  )

  const document = { lines: quote.lines, options: quote.options, discountAmount: quote.discountAmount }
  const choice = { optionId, addOnIds }
  const totals = proposalTotals(document, choice)
  const problem = proposalChoiceProblem(document, choice)
  const offered = availableAddOns(quote.lines, optionId)

  return (
    <>
      <div className="flex items-center gap-3 border-b border-ink-200 px-4 py-3">
        <h2 id="office-accept-title" className="text-base font-semibold text-ink-900">
          Record the customer's yes
        </h2>
        <div className="ml-auto">
          <ModalCloseButton onClose={onClose} />
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        {quote.options.length > 0 ? (
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1 text-xs font-medium text-ink-700">Which option</legend>
            {quote.options.map((option) => {
              const price = totals.packages.find((entry) => entry.optionId === option.id)
              return (
                <label
                  key={option.id}
                  className="flex cursor-pointer items-baseline gap-2 rounded-md px-2 py-1.5 text-[13px] ring-1 ring-ink-200 has-[:checked]:ring-ink-900"
                >
                  <input
                    type="radio"
                    name="office-accept-option"
                    className="accent-ink-900"
                    checked={optionId === option.id}
                    onChange={() => {
                      setOptionId(option.id)
                      const still = new Set(availableAddOns(quote.lines, option.id).map((line) => line.id))
                      setAddOnIds((current) => current.filter((id) => still.has(id)))
                    }}
                  />
                  <span className="flex-1 text-ink-900">{option.name}</span>
                  <span className="tabular-nums text-ink-700">{money(price?.totalIncTax ?? 0)}</span>
                </label>
              )
            })}
          </fieldset>
        ) : null}

        {offered.length > 0 ? (
          <fieldset className="flex flex-col gap-1">
            <legend className="mb-1 text-xs font-medium text-ink-700">Extras they want</legend>
            {offered.map((line) => (
              <label key={line.id} className="flex cursor-pointer items-baseline gap-2 px-2 py-1 text-[13px]">
                <input
                  type="checkbox"
                  className="accent-ink-900"
                  checked={addOnIds.includes(line.id)}
                  onChange={(event) =>
                    setAddOnIds((current) =>
                      event.target.checked ? [...current, line.id] : current.filter((id) => id !== line.id),
                    )
                  }
                />
                <span className="flex-1 text-ink-800">{line.description}</span>
                <span className="tabular-nums text-ink-700">+{money(line.lineTotalIncTax)}</span>
              </label>
            ))}
          </fieldset>
        ) : null}

        <Field label="Who said yes" htmlFor="office-accept-name" hint={`Left blank, it is recorded as ${quote.job.client.name}.`}>
          <Input id="office-accept-name" value={name} onChange={(event) => setName(event.target.value)} />
        </Field>

        <div className="flex items-baseline justify-between border-t border-ink-100 pt-3">
          <span className="text-[13px] font-semibold text-ink-900">Accepted total</span>
          <span className="text-lg font-bold tabular-nums text-ink-900">{money(totals.chosen.totalIncTax)}</span>
        </div>

        {respond.error ? (
          <p role="alert" className="text-xs text-red-600">
            {errorMessage(respond.error)}
          </p>
        ) : null}
      </div>

      <div className="flex justify-end gap-2 border-t border-ink-200 px-4 py-3">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={respond.isPending}
          disabled={problem !== null}
          title={problem ?? undefined}
          onClick={() =>
            respond.mutate({
              id: quote.id,
              outcome: 'ACCEPTED',
              optionId,
              addOnLineIds: addOnIds,
              ...(name.trim() ? { acceptedByName: name.trim() } : {}),
            })
          }
        >
          Accept and start the work
        </Button>
      </div>
    </>
  )
}
